import { IconButton, Paper, Typography, useTheme } from "@mui/material";
import React, { useState } from "react";
import { DeleteForever } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

import { QueryMonitor } from "system/QueryMonitor";
import QueryMonitorDialog from "./QueryMonitorDialog";
import { Box, Stack } from "@mui/system";
import ConfirmationDialog from "components/dialogs/ConfirmationDialog";
import { useSnackbar } from "notistack";
import ISORelativeDateTime from "components/formatting/ISORelativeDateTime";

interface QueryMonitorProps {
  onChange: () => void;
  queryMonitor: QueryMonitor;
}

export default function QueryMonitorRow(props: QueryMonitorProps): React.JSX.Element {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const { i18n, t } = useTranslation();

  const [showQueryMonitor, setShowQueryMonitor] = useState<boolean>(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(false);

  return (
    <Paper
      onClick={() => {
        // The Paper component also catches the click event from it's children. This prevents the
        // Paper click handler from overriding other click events.
        !showQueryMonitor && !showDeleteConfirmation && setShowQueryMonitor(true)
      }}
      sx={{
        cursor: "pointer",
        display: "block",
        padding: 1,
        "&:hover": {
          backgroundColor: theme.palette.action.hover,
        }
      }}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: "start",
          justifyContent: "space-between"
        }}>
        <Box>
          <Typography variant="h4">{props.queryMonitor.title}</Typography>
          <Typography variant="body1">{props.queryMonitor.description}</Typography>
        </Box>
        <IconButton
          color='error' 
          onClick={(event) => {
            event.stopPropagation();
            setShowDeleteConfirmation(true);
          }}>
          <DeleteForever />
        </IconButton>
      </Stack>
      <Stack direction="row">
        <Stack
          direction="row"
          sx={{
            alignItems: "baseline",
            flex: 1
          }}>
          {(props.queryMonitor.alert_sensitivity !== "none" || props.queryMonitor.last_alert) &&
            <>
              <Typography variant="h5" sx={{
                marginRight: 1
              }}>
                {t("query-monitor-row.last-alert")}
              </Typography>
              {props.queryMonitor.last_alert &&
                <ISORelativeDateTime isoString={props.queryMonitor.last_alert} />
                ||
                <>{t("query-monitor-row.last-alert-never")}</>
              }
            </>
          }
        </Stack>
        <Stack
          direction="row"
          sx={{
            alignItems: "baseline",
            flex: 1,
            justifyContent: "center"
          }}>
          {(props.queryMonitor.alert_sensitivity !== "none" || props.queryMonitor.last_alert_check) &&
            <>
              <Typography variant="h5" sx={{
                marginRight: 1
              }}>
              {t("query-monitor-row.last-checked")}
              </Typography>
              {props.queryMonitor.last_alert_check &&
                <ISORelativeDateTime isoString={props.queryMonitor.last_alert_check} />
                ||
                <>{t("query-monitor-row.last-checked-never")}</>
              }
            </>
          }
        </Stack>
        <Stack
          direction="row"
          sx={{
            alignItems: "baseline",
            flex: 1,
            justifyContent: "end"
          }}>
          {(props.queryMonitor.digest_weekdays.length !== 0 || props.queryMonitor.last_digest) && 
            <>
              <Typography variant="h5" sx={{
                marginRight: 1
              }}>
                {t("query-monitor-row.last-digest")}
              </Typography>
              {props.queryMonitor.last_digest &&
                <ISORelativeDateTime isoString={props.queryMonitor.last_digest} />
                ||
                <>{t("query-monitor-row.last-digest-never")}</>
              }
            </>
          }
        </Stack>
      </Stack>
      {showQueryMonitor &&
        <QueryMonitorDialog
          onChange={() => props.onChange()}
          onClose={() => setShowQueryMonitor(false)}
          open={showQueryMonitor} 
          queryMonitor={props.queryMonitor} />
      }
      <ConfirmationDialog
          isOpen={showDeleteConfirmation}
          title={t("query-monitor-row.delete-confirmation.title")}
          description={t("query-monitor-row.delete-confirmation.description", {query_monitor_title: props.queryMonitor.title})}
          onClose={async (isConfirmed:boolean) => {
            if (isConfirmed) {
              await QueryMonitor.delete(props.queryMonitor.id);
              enqueueSnackbar(t("query-monitor-row.delete-confirmation.delete-success-toast"), { variant: "success" });
              props.onChange();
            } else {
              setShowDeleteConfirmation(false);
            }
          }} />
    </Paper>
  );
}